import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CandidateLayout } from "@/components/candidate/CandidateLayout";
import { PartnerPageEditor } from "@/components/admin/partner/PartnerPageEditor";
import { DuplicatePageDialog } from "@/components/admin/partner/DuplicatePageDialog";
import { CreatePageDialog } from "@/components/admin/partner/CreatePageDialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Handshake, Edit, Copy, Plus } from "lucide-react";
import { useNoIndex } from "@/hooks/useCanonicalTag";

export default function PartnerPages() {
  useNoIndex();
  const [editingPageId, setEditingPageId] = useState<string | null>(null);
  const [duplicatingPageId, setDuplicatingPageId] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const { data: pages = [] } = useQuery({
    queryKey: ["admin-partner-pages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partner_landing_pages")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  if (editingPageId) {
    return (
      <CandidateLayout>
        <div className="p-4 sm:p-6 max-w-5xl mx-auto">
          <PartnerPageEditor pageId={editingPageId} onBack={() => setEditingPageId(null)} />
        </div>
      </CandidateLayout>
    );
  }

  return (
    <CandidateLayout>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Handshake className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold">Landing Pages de Parceiros</h1>
          </div>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-1" /> Nova LP
          </Button>
        </div>
        <p className="text-muted-foreground">
          Gerencie as landing pages de parcerias. Edite o conteúdo, duplique ou crie novas páginas.
        </p>

        <div className="grid gap-4">
          {pages.map(page => (
            <Card key={page.id} className="hover:shadow-md transition-shadow">
              <CardContent className="flex items-center justify-between p-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{page.page_title || page.slug}</h3>
                    <Badge variant={page.is_active ? "default" : "secondary"}>
                      {page.is_active ? "Ativa" : "Inativa"}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">/{page.slug}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setDuplicatingPageId(page.id)}>
                    <Copy className="h-4 w-4 mr-1" /> Duplicar
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setEditingPageId(page.id)}>
                    <Edit className="h-4 w-4 mr-1" /> Editar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {pages.length === 0 && (
            <p className="text-center text-muted-foreground py-8">Nenhuma landing page cadastrada.</p>
          )}
        </div>
      </div>

      <DuplicatePageDialog
        open={!!duplicatingPageId}
        onOpenChange={(open) => !open && setDuplicatingPageId(null)}
        sourcePageId={duplicatingPageId || ""}
        onSuccess={(newId) => setEditingPageId(newId)}
      />

      <CreatePageDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={(newId) => setEditingPageId(newId)}
      />
    </CandidateLayout>
  );
}
